
import moment from 'moment';
import * as React from 'react';
import { renderToString } from 'react-dom/server';

import { Activity } from '../../../interfaces/activity';
import { ACTIVITY_PACK_TYPES, NOT_APPLICABLE, Tooltip, assignedBadgeIconWhite, getIconForActivityClassification } from '../../Shared';
import { DROP_AFTER, DROP_BEFORE } from '../../Shared/components/shared/sortableList';

export const ALL_FLAGS = 'all flags';
export const ALL_DIAGNOSTICS = 'all diagnostics';
export const ALL_PACKS = 'all packs';
export const ARCHIVED_FLAG = 'archived';
export const NOT_ARCHIVED_FLAG = 'not archived';
export { DROP_AFTER, DROP_BEFORE };

const SECONDARY_PACK_TYPES = ['Starter', 'Intermediate', 'Advanced', 'ELL', 'Fishtank Learning', NOT_APPLICABLE];

export const packTypeOptionGroups = [
  [ALL_PACKS, ...ACTIVITY_PACK_TYPES.map(packType => packType.name)],
  SECONDARY_PACK_TYPES
];

const conceptMaxWidth = '152px';

function categoryNamesForPackType(packType: string) {
  const primaryPackType = ACTIVITY_PACK_TYPES.find(({ name }) => name === packType);
  return primaryPackType?.types || [packType];
}

export function matchesPackType(unitTemplate, packType: string) {
  if (packType === ALL_PACKS) { return true }

  const { unit_template_category } = unitTemplate;
  if (packType === NOT_APPLICABLE) { return !unit_template_category }

  return !!unit_template_category && categoryNamesForPackType(packType).includes(unit_template_category.name);
}

export function moveUnitTemplate({ unitTemplates, movedId, targetId, position }) {
  const sorted = sortUnitTemplates([...unitTemplates]);
  const movedUnitTemplate = sorted.find(({ id }) => String(id) === String(movedId));
  const rest = sorted.filter(({ id }) => String(id) !== String(movedId));
  const targetIndex = rest.findIndex(({ id }) => String(id) === String(targetId));
  if (!movedUnitTemplate || targetIndex === -1) { return sorted }

  const insertAt = position === DROP_BEFORE ? targetIndex : targetIndex + 1;
  const reordered = [...rest.slice(0, insertAt), movedUnitTemplate, ...rest.slice(insertAt)];
  return reordered.map((unitTemplate, orderNumber) => ({ ...unitTemplate, order_number: orderNumber }));
}

export function changedOrderNumbers({ previous, updated }) {
  return updated
    .filter(({ id, order_number }) => previous.find(unitTemplate => unitTemplate.id === id)?.order_number !== order_number)
    .map(({ id, order_number }) => ({ id, order_number }));
}

export function sortUnitTemplates (list) {
  return list.sort((bp1, bp2) => {
    // Group archived activities at the bottom of the list (they automatically get a higher order number
    // than any unarchived activity)
    if (bp1.flag.toLowerCase() === ARCHIVED_FLAG && bp2.flag.toLowerCase() !== ARCHIVED_FLAG) {
      return 1
    } else if (bp2.flag.toLowerCase() === ARCHIVED_FLAG && bp1.flag.toLowerCase() !== ARCHIVED_FLAG) {
      return -1
    }
    return bp1.order_number - bp2.order_number
  })
}

export function orderedUnitTemplates({
  diagnostic,
  fetchedData,
  flag,
  packType = ALL_PACKS,
  searchByActivityPack,
  searchInput
}) {
  let filteredData = fetchedData.filter(unitTemplate => matchesPackType(unitTemplate, packType))
  if (flag === NOT_ARCHIVED_FLAG) {
    filteredData = filteredData.filter(data => data.flag !== ARCHIVED_FLAG.toLowerCase())
  } else if (flag !== ALL_FLAGS) {
    filteredData = filteredData.filter(data => data.flag === flag.toLowerCase())
  }

  if (searchInput !== '' && searchByActivityPack) {
    filteredData = filteredData.filter(activity => {
      const { name } = activity;
      return name.toLowerCase().includes(searchInput.toLowerCase());
    })
  }

  if (searchInput !== '' && !searchByActivityPack) {
    filteredData = filteredData.filter(value => {
      return (
        value.activities && value.activities.map(x => x.name || '').some(y => y.toLowerCase().includes(searchInput.toLowerCase()))
      );
    })
  }

  if (diagnostic !== ALL_DIAGNOSTICS) {
    filteredData = filteredData.filter(value => {
      return (
        value.diagnostic_names && value.diagnostic_names.some(y => y.toLowerCase().includes(diagnostic.toLowerCase()))
      );
    })
  }

  return sortUnitTemplates(filteredData)
}

export const ActivityFocusHeaderLink = ({ className }: { className: string }) => (
  <Tooltip
    tooltipText="Activity Focus"
    tooltipTriggerText={<a className={className} href={`${window.postbuildEnv.DEFAULT_URL}/cms/attributes_manager/activity_foci`} rel="noopener noreferrer" target="_blank">Activity Focus</a>}
    tooltipTriggerTextStyle={{ maxWidth: conceptMaxWidth }}
  />
);

export const unitTemplateDataTableFields = [
  { name: "", attribute:"addActivity", width: "32px", noTooltip: true },
  { name: "Name", attribute:"name", width: "208px", rowSectionClassName: 'tabbable-field', noTooltip: true },
  { name: "Description", attribute:"description", width: "232px", rowSectionClassName: 'tooltip-section description-section', noTooltip: true },
  { name: "In packs", attribute:"inPacks", width: "232px", rowSectionClassName: 'in-packs-section', noTooltip: true },
  { name: "Flag", attribute:"flag", width: "80px", noTooltip: true },
  { name: "Readability", attribute:"readability", width: "64px", noTooltip: true },
  { name: <ActivityFocusHeaderLink className="data-link focus-on-light" />, attribute:"concept", width: conceptMaxWidth, noTooltip: true },
  { name: "Tool", attribute:"tool", width: "88px", noTooltip: true },
  { name: "", attribute:"edit", width: "88px", rowSectionClassName: 'tabbable-field', noTooltip: true }
];

export function displayInPacksInfo(unitTemplates) {
  return unitTemplates.map(unitTemplate => {
    const { id, name } = unitTemplate;
    return <a className="data-link focus-on-light" href={editActivityPackLink(id)} key={id} rel="noopener noreferrer" target="_blank">{name}</a>;
  });
}

const ActivityRowButton = ({ activity, handleClick, type }) => {
  const buttonStyle = type === 'add' ? 'add-activity-button' : 'remove-activity-button'
  const buttonText = type === 'add' ? '+' : '-'

  function handleButtonClick() { handleClick(activity) }

  return <button className={`${buttonStyle} interactive-wrapper focus-on-light`} onClick={handleButtonClick} type="button">{buttonText}</button>
}

export function unitTemplateActivityRows({ activities, handleClick, type }) {
  return activities.map(activity => {
    const { id, name, flags, activity_focus_name, classification, readability_grade_level, description, unit_templates } = activity;
    return {
      id,
      addActivity: <ActivityRowButton activity={activity} handleClick={handleClick} type={type} />,
      name: <a className="data-link focus-on-light" href={previewActivityLink(id)} rel="noopener noreferrer" target="_blank">{name}</a>,
      description: description || NOT_APPLICABLE,
      inPacks: displayInPacksInfo(unit_templates),
      flag: flags && flags.length ? flags.join(', ') : NOT_APPLICABLE,
      readability: readability_grade_level || NOT_APPLICABLE,
      concept: activity_focus_name || NOT_APPLICABLE,
      tool: classification && classification.id ? getIconForActivityClassification(classification.id) : NOT_APPLICABLE,
      edit: classification && classification.id ? <a className="data-link focus-on-light" href={editActivityLink(classification.id, id)} rel="noopener noreferrer" target="_blank">edit</a> : NOT_APPLICABLE
    }
  });
}

export const editActivityLink = (classificationId, activityId) => `${window.postbuildEnv.DEFAULT_URL}/cms/activity_classifications/${classificationId}/activities/${activityId}/edit`;

export const editActivityPackLink = (unitTemplateId) => `${window.postbuildEnv.DEFAULT_URL}/cms/unit_templates/${unitTemplateId}/edit`;

export const previewActivityLink = (activityId) => `${window.postbuildEnv.DEFAULT_URL}/activity_sessions/anonymous?activity_id=${activityId}`;

export const validateUnitTemplateForm = ({ activityPackFlag, activityPackName, activityPackType }) => {
  const errors = {};
  if(!activityPackFlag) {
    errors['activityPackFlag'] = 'Activity pack flag is required.'
  }
  if(!activityPackName) {
    errors['activityPackName'] = 'Activity pack name is required.'
  }
  if(!activityPackType) {
    errors['activityPackType'] = 'Activity pack type is required.'
  }
  return errors;
}

export const renderActivityPackTooltipElement = (data) => {
  if (!data) { return }
  const { activities, } = data;
  const table = (
    <table className="activity-tooltip-table">
      <tbody>
        <tr>
          <th className="activity-column">Activity</th>
          <th className="tool-column">Tool</th>
          <th className="grade-level-range-column">Target Grade Range</th>
        </tr>
        {activities && activities.length && activities.map((activity: Activity) => {
          const { name, grade_band_range, classification } = activity
          return(
            <tr key={name}>
              <td>{name}</td>
              <td>{classification.name}</td>
              <td>{grade_band_range}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
  return renderToString(table)
}

export const renderPreviouslyAssignedActivitiesTooltipElement = (data) => {
  if (!data) { return }

  const table = (
    <table className="previously-assigned-activities-table">
      <section className="assigned-activity-info-section">
        <img alt={assignedBadgeIconWhite.alt} src={assignedBadgeIconWhite.src} />
        <section className="assigned-activity-text-section">
          <p>You&apos;ve previously assigned this activity in another activity pack. You can remove individual activities from a pre-created pack on the next page.</p>
          <br />
          <p>Tip: If you want to add individual students to a previously created Activity Pack, go to &quot;Manage Activities&quot; to add students.</p>
        </section>
      </section>
      <tbody>
        <tr>
          <th>Assigned in Other Activity Packs</th>
          <th>Date Assigned</th>
          <th>Classes</th>
          <th>Students</th>
        </tr>
        {data.length && data.map((unit) => {
          const { name, assigned_date, classrooms, students, } = unit;
          return(
            <tr key={name}>
              <td>{name}</td>
              <td>{moment(assigned_date).format("MM/DD/YY")}</td>
              <td>{classrooms.map(classroom => (<p key={classroom}>{classroom}</p>))}</td>
              <td>
                {students.map((student, i) => {
                  const { assigned_student_count, total_student_count } = student;
                  const label = total_student_count === 1 ? 'student' : 'students';
                  return <p key={i}>{`${assigned_student_count}/${total_student_count} ${label}`}</p>
                })}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
  return renderToString(table)
}
